// Pure, React-free reshaping of calculateRackLayout's output. Split out of
// features/rack-view (CONF-140 Phase 5 controller ruling P3) so Task 4 (System
// Slide) can import the same slot model without reaching into another feature
// — features may not import each other, but both may import shared/.
import { calculateRackLayout } from 'utils/RealisticRackVisualization';
import type { FlatConfig } from 'domain/index';

export type RackUnitModel = {
  type: 'dNode' | 'fNode' | 'sNode' | 'jbod' | 'jbod-8u';
  heightU: number;
  label: string;
  ssdInfo?: string;
  jbodLabel?: string;
  isJbodOnly?: boolean;
};

type RackSlot = RackUnitModel | { type: 'empty' };

export type RackModel = {
  index: number;
  size: number;
  units: Array<{ position: number; unit: RackUnitModel }>;
};

/** The occupied slot types calculateRackLayout emits; every other slot is `{type:'empty'}`,
 *  including the filler slots after a multi-U unit's first slot. */
export const OCCUPIED_TYPES: ReadonlySet<RackUnitModel['type']> = new Set([
  'dNode', 'fNode', 'sNode', 'jbod', 'jbod-8u',
]);

// Lives in rackSizes.ts (React-free, for the MCP Lambda); re-exported for UI importers.
export { RACK_SIZE_OPTIONS } from './rackSizes';

/** Pixel height of one rack unit. Declared once here (P8) — RackUnit, RackView
 *  and any future consumer (Task 4) import it rather than redeclare it. */
export const U_PX = 18;

function isOccupied(slot: RackSlot | undefined): slot is RackUnitModel {
  return slot !== undefined && OCCUPIED_TYPES.has(slot.type as RackUnitModel['type']);
}

/** calculateRackLayout (utils/RealisticRackVisualization.jsx) is imported unchanged —
 *  rackSlidePPTGenerator shares it, so its return shape is contractual. A unit occupies
 *  `heightU` slots but only the first slot carries the object; the rest stay
 *  `{type:'empty'}`. This reshapes racks × slots into racks × occupied-units-only, so no
 *  consumer has to re-derive the packing rule to avoid drawing the filler slots as empty
 *  bays.
 *
 *  Slot index 0 is the TOP of the rack, not the bottom — verified against both
 *  contractual consumers of this same array: RealisticRackVisualization.jsx:198 positions
 *  each unit with `top: index * unitHeight`, and its markers (line 236/240) label row i as
 *  `${rackSize - i}U`; rackSlidePPTGenerator.js:151 computes `y = slotIndex * unitH` with
 *  SVG y growing downward. (An earlier draft of this module's spec asserted the array was
 *  indexed bottom-of-array-first; that was wrong — this is the one place that claim ever
 *  appeared, and it never should again.) */
export function toRackModels(config: FlatConfig, productDb: any, rackSize: number): RackModel[] {
  const layout = calculateRackLayout(config, productDb, rackSize) as RackSlot[][];
  return layout.map((slots, index) => ({
    index,
    size: slots.length,
    units: slots.reduce<Array<{ position: number; unit: RackUnitModel }>>((acc, slot, position) => {
      if (isOccupied(slot)) acc.push({ position, unit: slot });
      return acc;
    }, []),
  }));
}
